import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SearchPatientFormValues } from 'src/components/SearchPatientInfoFormGroup/SearchPatientInfoFormGroup';

import { Api } from 'src/models/Api.model';
import { OrderStatusEnum } from 'src/models/GalenData/Order.model';
import { PageOfDeviceDataView } from 'src/models/GalenData/PageOfDeviceDataView.model';
import { I18NextNS } from 'src/models/Language.model';

import useHandleHttpRequestError from 'src/hooks/useHandleHttpRequestError';

const POLLING_INTERVAL_MS = 5 * 60 * 1000;

const DEFAULT_PAGE_SIZE = 10;

interface UsePollFilteredOrdersParams {
  shouldPoll: boolean;
  getFilteredOrdersCore: (params: {
    siteId: string | undefined;
    pageNumber: number;
    searchValues?: SearchPatientFormValues;
    status: OrderStatusEnum[];
    pageSize: number;
  }) => Promise<PageOfDeviceDataView<any> | undefined>;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  selectedSiteId: string | undefined;
  searchValues: SearchPatientFormValues | undefined;
  currentSelectedStatus: OrderStatusEnum[];
  resetToken: number;
}

export const usePollFilteredOrders = ({
  shouldPoll,
  getFilteredOrdersCore,
  currentPage,
  setCurrentPage,
  selectedSiteId,
  searchValues,
  currentSelectedStatus,
  resetToken,
}: UsePollFilteredOrdersParams) => {
  const pollingIntervalRef = useRef<number | null>(null);
  const isMounted = useRef(false);

  const [isPolling, setIsPolling] = useState(false);

  const {
    t,
    i18n: { language },
  } = useTranslation(I18NextNS.COMMON);

  const { handleHttpRequestError } = useHandleHttpRequestError();

  const poll = useCallback(async () => {
    try {
      setIsPolling(true);
      const result = await getFilteredOrdersCore({
        siteId: selectedSiteId,
        pageNumber: currentPage - 1,
        searchValues,
        status: currentSelectedStatus,
        pageSize: DEFAULT_PAGE_SIZE,
      });

      if (!result) return;

      if (!result.content) {
        if (currentPage !== 1) {
          setCurrentPage(1);
        }
        return;
      }

      if (currentPage > result.totalPages) {
        setCurrentPage(result.totalPages);
        await getFilteredOrdersCore({
          siteId: selectedSiteId,
          pageNumber: result.totalPages - 1,
          searchValues,
          status: currentSelectedStatus,
          pageSize: DEFAULT_PAGE_SIZE,
        });
        return;
      }
    } catch (error) {
      const errorMessage = handleHttpRequestError(error);
      Api.alertBox({
        title: t('bootbox.title.Error'),
        message: errorMessage,
        locale: language,
        type: 'error',
      });
    } finally {
      setIsPolling(false);
    }
  }, [
    getFilteredOrdersCore,
    selectedSiteId,
    currentPage,
    searchValues,
    currentSelectedStatus,
    setCurrentPage,
    handleHttpRequestError,
    t,
    language,
  ]);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }

    if (!shouldPoll) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      return;
    }

    poll();
    pollingIntervalRef.current = window.setInterval(poll, POLLING_INTERVAL_MS);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [
    shouldPoll,
    getFilteredOrdersCore,
    currentPage,
    setCurrentPage,
    selectedSiteId,
    searchValues,
    currentSelectedStatus,
    resetToken,
    poll,
  ]);

  return {
    isPolling,
  };
};
